'use client';

import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { trackCtaClick, resolvePageTemplate, type CtaPosition } from '@/lib/ctaTracking';

interface TrackedCtaLinkProps {
  href: string;
  position: CtaPosition;
  /** Falls back to the rendered text; set explicitly when children aren't a plain string. */
  ctaText?: string;
  variant?: string;
  location?: string;
  pagePair?: string;
  className?: string;
  children: React.ReactNode;
}

/**
 * A `next/link` that emits `cta_clicked` before navigating.
 *
 * Exists as its own client component so the marketing page templates that use
 * it can stay server-rendered - only the link itself needs to be interactive.
 */
export default function TrackedCtaLink({
  href,
  position,
  ctaText,
  variant,
  location,
  pagePair,
  className,
  children,
}: TrackedCtaLinkProps) {
  const pathname = usePathname() ?? '';

  const handleClick = () => {
    trackCtaClick({
      template: resolvePageTemplate(pathname),
      page_path: pathname,
      cta_position: position,
      cta_text: ctaText ?? (typeof children === 'string' ? children : ''),
      destination: href,
      variant,
      location,
      page_pair: pagePair,
    });
  };

  return (
    <Link href={href} className={className} onClick={handleClick}>
      {children}
    </Link>
  );
}
